import { Suspense, useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/I18nProvider'
import { useApp } from '../store/AppProvider'
import { Avatar, Badge, Button, Icon, IconButton, Spinner } from './ui'
import { Logo, UserMenu } from './PublicLayout'
import { OnboardingWidget } from './OnboardingWidget'
import { RouteError } from './RouteError'

/**
 * Sidebar ruang kerja. TIGA tingkat, bukan dua.
 *
 * Sampai sekarang penyaringnya biner: `platform: true` atau tidak. Itu cukup
 * selama yang masuk ke sini hanya admin. Sejak pakar ikut punya ruang kerja,
 * menu yang sama akan menawarkan hal-hal yang bakal ditolak servernya dengan
 * 403 — dan menu yang bisa diklik tapi selalu gagal itu lebih buruk daripada
 * menu yang tidak ada: orang menyimpulkan aplikasinya rusak, bukan bahwa ia
 * memang tidak berhak.
 *
 * Penandanya:
 *   `pakar: true`    — pakar boleh melihatnya (admin juga).
 *   `platform: true` — hanya admin platform.
 *   tanpa penanda    — kedua jenis admin, bukan pakar.
 *
 * Daftar ini HARUS cocok dengan pembagian rute di backend
 * (internal/api/router.go: RequireWorkspace vs RequireAdmin). Kalau keduanya
 * berbeda, bedanya muncul sebagai 403 saat diklik, bukan sebagai kesalahan yang
 * kelihatan saat menulis kode.
 */
const NAV = [
  { to: '/admin', icon: 'space_dashboard', key: 'admin.dashboard', end: true, pakar: true },
  // Panduan pengisian konten. Ditaruh paling atas karena pembacanya adalah
  // pakar yang baru pertama kali masuk dan belum tahu harus mulai dari mana.
  { to: '/admin/panduan', icon: 'menu_book', key: 'admin.guide', pakar: true },
  { to: '/admin/courses', icon: 'video_library', key: 'admin.courses', pakarKey: 'admin.myCourses', pakar: true },
  { to: '/admin/instructors', icon: 'school', key: 'admin.instructors' },
  { to: '/admin/live-classes', icon: 'sensors', key: 'admin.live', pakar: true },
  { to: '/admin/qa', icon: 'forum', key: 'admin.qa', pakar: true },
  { to: '/admin/consultations', icon: 'event_available', key: 'admin.consult', pakar: true },
  { to: '/admin/consultation-slots', icon: 'more_time', key: 'admin.slots', pakar: true },
  { to: '/admin/enrollments', icon: 'how_to_reg', key: 'admin.enrollments', pakar: true },
  { to: '/admin/reviews', icon: 'reviews', key: 'admin.reviews', pakar: true },
  { to: '/admin/certificates', icon: 'workspace_premium', key: 'admin.certificates', pakar: true },
  { to: '/admin/coupons', icon: 'local_offer', key: 'admin.coupons' },
  { to: '/admin/notifications', icon: 'campaign', key: 'admin.notifications', pakar: true },
  { divider: 'admin.platformOnly', platform: true },
  { to: '/admin/providers', icon: 'apartment', key: 'admin.providers', platform: true },
  { to: '/admin/categories', icon: 'category', key: 'admin.categories', platform: true },
  // Price tiers DIBUANG dari menu. Ia lahir untuk satu masalah: produk IAP
  // harus didaftarkan manual di App Store, jadi harga kelas dipaksa memilih
  // dari set tetap yang tiap anggotanya punya SKU. Sejak kelas berharga
  // KREDIT, yang butuh SKU adalah paket top-up — bukan kelas. Jadi sambungan
  // Course → PriceTier bukan sekadar belum dibuat; ia tidak akan pernah
  // dibuat. Model, tabel, dan endpoint-nya sengaja dibiarkan utuh di Go
  // (lihat internal/models/models.go) supaya keputusan ini murah dibalik.
  { to: '/admin/certificate-templates', icon: 'design_services', key: 'admin.certTemplates', platform: true },
  { to: '/admin/audit-logs', icon: 'history', key: 'admin.audit', platform: true },
]

/**
 * visibleNav menyaring menu untuk satu peran, lalu MEMBUANG pemisah yang tidak
 * lagi punya isi di bawahnya.
 *
 * Pemisah yatim ("HANYA PLATFORM" tanpa satu pun menu sesudahnya) terlihat
 * seperti bagian yang gagal dimuat.
 */
export function visibleNav(nav, { isPlatformAdmin, isPakar }) {
  const kept = nav.filter((i) => {
    if (i.platform) return isPlatformAdmin
    if (isPakar) return !!i.pakar
    return true
  })
  return kept.filter((i, idx) => {
    if (!i.divider) return true
    const next = kept[idx + 1]
    return !!next && !next.divider
  })
}

const ROLE_LABEL = {
  platform_admin: 'adm.role.platform',
  provider_admin: 'adm.role.provider',
  instructor: 'adm.role.instructor',
  student: 'adm.role.student',
}
/** Translation key for a role. Unknown roles fall back to the learner label. */
export const roleLabelKey = (role) => ROLE_LABEL[role] || 'adm.role.student'

export function AdminLayout() {
  const { t } = useI18n()
  const { user, isPlatformAdmin, isAdmin, isInstructor, logout, setLoginOpen } = useApp()
  const [open, setOpen] = useState(false)
  const loc = useLocation()
  const nav = useNavigate()
  useEffect(() => setOpen(false), [loc.pathname])

  // Pakar SEKARANG punya ruang kerja di sini, jadi gerbangnya bukan lagi
  // "hanya admin". Yang ditolak adalah murid dan tamu.
  const isPakar = !!isInstructor && !isAdmin
  if (!isAdmin && !isInstructor) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-surface-sub">
        <div className="bg-white rounded-xl2 shadow-card p-8 max-w-md text-center">
          <span className="mx-auto mb-4 grid place-items-center w-14 h-14 rounded-2xl bg-brand-soft text-brand"><Icon name="admin_panel_settings" size={28} /></span>
          <h1 className="text-xl font-extrabold">{t('adm.gate.title')}</h1>
          {/* Dulu tertulis "gunakan akun demo di modal login" — pintasan akun demo
              sudah dihapus, jadi teksnya menyesatkan. */}
          {/* Cabang pakar dihapus dari sini: pakar sekarang LOLOS gerbang ini
              dan punya ruang kerjanya sendiri. Yang tersisa di halaman ini
              hanya murid dan tamu, dan keduanya butuh ajakan yang sama. */}
          <p className="text-sm text-muted mt-2 kx-pretty">{t('adm.gate.sub')}</p>
          <div className="flex gap-2 justify-center mt-5">
            <Button icon="login" onClick={() => setLoginOpen(true)}>{t('adm.gate.login')}</Button>
            <Button kind="outline" onClick={() => nav('/')}>{t('adm.gate.storefront')}</Button>
          </div>
        </div>
      </div>
    )
  }

  const items = visibleNav(NAV, { isPlatformAdmin, isPakar })

  return (
    <div className="min-h-screen bg-surface-sub lg:grid" style={{ gridTemplateColumns: '250px 1fr' }}>
      {/* sidebar */}
      {/* Kelas "tutup" dibatasi ke breakpoint max-lg, bukan dilawan dengan kelas
          translate lain di lg. Tailwind mengurutkan varian arah (ltr/rtl)
          SETELAH varian breakpoint, dan spesifisitas keduanya sama, jadi aturan
          arah selalu menang: sidebar ikut tergeser ke luar layar di desktop dan
          kolom 250px-nya tampak kosong melompong.
          Catatan: jangan tulis nama utility-nya lengkap di komentar — pemindai
          Tailwind membacanya dan menghasilkan aturan CSS yatim. */}
      <aside className={`fixed lg:sticky top-0 z-50 h-screen w-[250px] bg-ink text-white flex flex-col transition-transform ${open ? 'translate-x-0' : 'max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full'}`}>
        <div className="h-16 px-4 flex items-center justify-between border-b border-white/10">
          <Logo light size="sm" />
          <IconButton icon="close" label="close" kind="ghost" size={32} className="lg:hidden text-white" onClick={() => setOpen(false)} />
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
          {items.map((i, idx) => i.divider ? (
            <p key={idx} className="px-3 pt-5 pb-2 text-[10px] font-black uppercase tracking-[0.12em] text-white/35">{t(i.divider)}</p>
          ) : (
            <NavLink key={i.to} to={i.to} end={i.end}
              className={({ isActive }) => `flex items-center gap-2.5 h-10 px-3 rounded-xl text-[14px] font-bold transition ${isActive ? 'bg-brand text-white shadow-[0_4px_12px_rgba(241,91,42,.35)]' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}>
              {/* Label bisa berbeda untuk pakar: "Kelas" berarti seluruh
                  katalog bagi admin, tapi bagi pakar isinya hanya kelasnya
                  sendiri — dan menyebutnya "Kelas" membuat katalog terasa
                  kosong, bukan tersaring. */}
              <Icon name={i.icon} size={19} />{t(isPakar && i.pakarKey ? i.pakarKey : i.key)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2.5 p-2 rounded-xl bg-white/5">
            <Avatar src={user.avatar_url} name={user.name} size={34} />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-extrabold truncate">{user.name}</span>
              {/* Email ikut ditampilkan: nama saja tidak menjawab "aku sedang
                  masuk sebagai akun yang mana", dan sesi basi di tab lain jadi
                  tak terlihat. */}
              <span className="block text-[11px] text-white/50 truncate" title={user.email}>{user.email}</span>
              {/* Peta, bukan terner: dulu apa pun yang bukan platform_admin
                  dilabeli "Admin provider" — termasuk role baru. */}
              <span className="block text-[11px] text-white/40 truncate">{t(roleLabelKey(user.role))}</span>
            </span>
            <IconButton icon="logout" label="logout" kind="ghost" size={30} className="text-white/70" onClick={logout} />
          </div>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-ink-deep/50 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* content */}
      <div className="min-w-0">
        {/* Isi header memakai pembungkus ber-lebar sama dengan isi halaman
            (max-w + mx-auto). Tanpa ini, badge scope menempel ke tepi kiri layar
            sementara judul halaman di bawahnya mulai jauh di dalam — dua sumbu
            kiri yang berbeda pada layar lebar. */}
        <header className="sticky top-0 z-30 h-16 bg-white/95 backdrop-blur border-b border-line">
          <div className="h-full max-w-[1400px] mx-auto flex items-center gap-3 px-4 lg:px-6">
            <IconButton icon="menu" label="menu" kind="ghost" className="lg:hidden" onClick={() => setOpen(true)} />
            {/* Nama lembaga datang dari `provider_name` milik API. Dulu tertulis
                "Scope: Baitul Hikmah Institute" secara hardcode — jadi admin
                lembaga MANA PUN diberi tahu ia sedang melihat data lembaga lain.
                Untuk admin yang datanya dipisah per lembaga, itu bukan salah
                kosmetik. Kalau namanya belum ada, lebih baik menyebut peran
                daripada mengarang nama. */}
            <Badge tone={isPlatformAdmin ? 'ink' : 'gold'} icon={isPlatformAdmin ? 'public' : 'apartment'}>
              {isPlatformAdmin ? t('adm.scope.all') : user.provider_name ? t('adm.scope.provider', { name: user.provider_name }) : t('adm.role.provider')}
            </Badge>
            <div className="ms-auto flex items-center gap-1.5">
              <Button as={Link} to="/" kind="outline" size="sm" icon="storefront" className="hidden sm:inline-flex">{t('adm.nav.storefront')}</Button>
              {/* Menu yang SAMA dengan storefront, bukan salinan.

                  Sebelumnya identitas hanya ada di kaki sidebar — dan sidebar
                  itu drawer di bawah lg, jadi di tablet dan ponsel tidak ada
                  satu pun tempat di layar yang menjawab "aku masuk sebagai
                  siapa". Satu-satunya jalan keluar pun tersembunyi di balik
                  tombol hamburger. */}
              <UserMenu />
            </div>
          </div>
        </header>
        {/* mx-auto: lebarnya dibatasi supaya baris teks tidak terlalu panjang untuk
            dibaca, TAPI sisa ruangnya dibagi rata kiri-kanan. Sebelumnya isi
            halaman mepet kiri dan menyisakan lubang kosong di kanan layar lebar. */}
        <div className="p-4 lg:p-6 max-w-[1400px] mx-auto">
          <RouteError key={loc.pathname}>
            <Suspense fallback={<div className="py-24 grid place-items-center text-muted"><Spinner size={26} /></div>}>
              <Outlet />
            </Suspense>
          </RouteError>
        </div>
        {/* Daftar langkah mengapung untuk admin baru. Posisinya fixed, jadi ikut
            tampil di seluruh rute /admin/*; komponennya sendiri yang memutuskan
            kapan tidak merender apa pun (mis. sudah ditutup permanen). */}
        <OnboardingWidget />
      </div>
    </div>
  )
}

export function PageHead({ title, sub, actions, breadcrumb }) {
  return (
    <div className="mb-5">
      {breadcrumb && (
        <nav className="flex items-center gap-1.5 text-[12px] text-muted mb-2">
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <Icon name="chevron_right" size={14} />}
              {b.to ? <Link to={b.to} className="hover:underline text-muted">{b.label}</Link> : <span>{b.label}</span>}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-[-0.02em]">{title}</h1>
          {sub && <p className="text-muted text-sm mt-1 kx-pretty">{sub}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  )
}
